import { useState, useMemo, Component } from 'react'
import type { ReactNode, ErrorInfo } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { SignIn, SignUp, useAuth, useUser, UserButton } from '@clerk/clerk-react'
import { dark } from '@clerk/themes'
import { GATEWAY_HTTP } from './config'
import { useQuery } from '@tanstack/react-query'
import { TerminalPanel } from './components/Terminal'
import { FleetDashboard, type Machine } from './components/FleetDashboard'

class ErrorBoundary extends Component<{ children: ReactNode }, { err: string | null }> {
  state = { err: null }
  static getDerivedStateFromError(e: Error) { return { err: e.message + '\n' + e.stack } }
  componentDidCatch(e: Error, info: ErrorInfo) { console.error('Fleet crash:', e, info) }
  render() {
    if (this.state.err) return (
      <div style={{ position: 'absolute', inset: 0, background: '#0d0007', color: '#f87171', padding: 32, fontFamily: 'monospace', fontSize: 12, overflowY: 'auto' }}>
        <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Render error — copy this and report it:</div>
        <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>{this.state.err}</pre>
      </div>
    )
    return this.props.children
  }
}

type Status = 'connecting' | 'connected' | 'disconnected'
type View   = 'fleet' | 'terminal'

interface Session {
  id: string
  machineId: string
  hostname: string
}

// ---------------------------------------------------------------------------
// Brand: Stelline logo icon
// ---------------------------------------------------------------------------
function StellIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="12" r="11" stroke="rgba(124,111,247,0.2)" strokeWidth="0.5" />
      <circle cx="12" cy="12" r="1"  fill="#7c6ff7" />
      <circle cx="12" cy="12" r="4"  stroke="rgba(124,111,247,0.3)" strokeWidth="0.5" />
      <line x1="12" y1="1"  x2="12" y2="5"  stroke="#7c6ff7"              strokeWidth="1"   strokeLinecap="round" />
      <line x1="12" y1="19" x2="12" y2="23" stroke="rgba(124,111,247,0.4)" strokeWidth="0.5" strokeLinecap="round" />
      <line x1="1"  y1="12" x2="5"  y2="12" stroke="rgba(124,111,247,0.4)" strokeWidth="0.5" strokeLinecap="round" />
      <line x1="19" y1="12" x2="23" y2="12" stroke="rgba(124,111,247,0.4)" strokeWidth="0.5" strokeLinecap="round" />
    </svg>
  )
}

function StellWordmark({ size = 14 }: { size?: number }) {
  const style: React.CSSProperties = {
    fontFamily: 'Inter, system-ui, sans-serif',
    fontWeight: 300,
    fontSize: size,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    lineHeight: 1,
  }
  return (
    <span style={style}>
      <span style={{ color: '#ffffff' }}>STELL</span>
      <span style={{ color: '#7c6ff7' }}>INE</span>
    </span>
  )
}

function StellLogo({ iconSize = 22, wordSize = 14, showTagline = true }: {
  iconSize?: number
  wordSize?: number
  showTagline?: boolean
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
      <StellIcon size={iconSize} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <StellWordmark size={wordSize} />
        {showTagline && (
          <span style={{
            fontFamily: 'Inter, system-ui, sans-serif',
            fontWeight: 300,
            fontSize: 8,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: '#3a3a5a',
            lineHeight: 1,
            whiteSpace: 'nowrap',
          }}>
            Memory · Trace · Resolve
          </span>
        )}
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Status dot
// ---------------------------------------------------------------------------
function StatusDot({ status, size = 6 }: { status: Status; size?: number }) {
  const color = {
    connected:    '#22c55e',
    connecting:   '#f59e0b',
    disconnected: '#ef4444',
  }[status]
  return (
    <span
      className={status === 'connecting' ? 'animate-pulse' : undefined}
      style={{ display: 'inline-block', width: size, height: size, borderRadius: '50%', background: color, flexShrink: 0 }}
    />
  )
}

// ---------------------------------------------------------------------------
// Sidebar section label
// ---------------------------------------------------------------------------
function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 500, letterSpacing: '0.08em',
      color: 'var(--text-dim)', textTransform: 'uppercase',
      padding: '8px 8px 6px',
    }}>
      {children}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Sidebar user row
// ---------------------------------------------------------------------------
function SidebarUser() {
  const { user } = useUser()
  const displayName = user?.firstName
    ? `${user.firstName}${user.lastName ? ' ' + user.lastName : ''}`
    : user?.username ?? 'User'
  const email = user?.primaryEmailAddress?.emailAddress ?? ''

  return (
    <div
      style={{ borderTop: '1px solid var(--border)', padding: '10px 12px' }}
      className="flex items-center gap-2.5"
    >
      <UserButton
        afterSignOutUrl="/sign-in"
        appearance={{
          elements: {
            avatarBox: { width: 28, height: 28 },
            userButtonPopoverCard: {
              background: '#0d0f1a',
              border: '1px solid var(--border)',
              boxShadow: '0 8px 32px rgba(0,0,0,0.7)',
            },
            userButtonPopoverActionButton: { color: 'var(--text-primary)' },
            userButtonPopoverActionButton__signOut: { color: 'var(--danger)' },
            userButtonPopoverFooter: { display: 'none' },
          },
        }}
      />
      <div className="min-w-0 flex flex-col gap-0.5">
        <span style={{ color: 'var(--text-primary)', fontSize: 13, fontWeight: 500, lineHeight: 1.2 }} className="truncate">
          {displayName}
        </span>
        <span style={{ color: 'var(--text-secondary)', fontSize: 11, lineHeight: 1.2 }} className="truncate">
          {email}
        </span>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Protected main app
// ---------------------------------------------------------------------------
function MainApp() {
  const { isSignedIn, isLoaded, getToken } = useAuth()

  const [activeView, setActiveView] = useState<View>('fleet')
  const [sessions, setSessions] = useState<Session[]>([])
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null)
  const [termStatus, setTermStatus] = useState<Status>('connecting')
  const [analysis, setAnalysis] = useState<{
    severity: string
    findings: string[]
    actions:  string[]
  } | null>(null)

  // Fleet data — polled every 30s, shared between sidebar badge and fleet dashboard
  const { data: machines = [], isLoading: machinesLoading, refetch: refetchMachines } = useQuery<Machine[]>({
    queryKey: ['machines'],
    queryFn: async () => {
      const token = await getToken()
      const res = await fetch(`${GATEWAY_HTTP}/api/machines`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      if (!res.ok) return []
      return res.json()
    },
    refetchInterval: 30_000,
    enabled: isSignedIn === true,
  })

  // Dedupe by (hostname, os, arch): two physical machines with the same
  // default hostname (e.g. "DESKTOP" or "ubuntu") but different OSes were
  // collapsing into one card. Within a single (hostname, os, arch) tuple
  // there can still be multiple rows from repeated token provisioning —
  // keep the most-recently-seen row in that case.
  //
  // No ghost-row filter: a token that was provisioned but never connected
  // is exactly what the user needs to see to realize the install failed.
  const visibleMachines = useMemo(() => {
    try {
      if (machines.length > 0) {
        console.log('[visibleMachines] raw sample:', machines[0], 'total:', machines.length)
      }
      const map = new Map<string, Machine>()
      for (const m of machines) {
        if (!m || typeof m !== 'object') continue
        const key = `${m.hostname ?? ''}|${m.os ?? ''}|${m.arch ?? ''}`
        const existing = map.get(key)
        if (!existing) {
          map.set(key, m)
        } else {
          const t = (s: string | null | undefined) => (s ? new Date(s).getTime() : 0)
          if (t(m.last_seen) > t(existing.last_seen)) map.set(key, m)
        }
      }
      return Array.from(map.values()).sort((a, b) => {
        if (a.status === 'online' && b.status !== 'online') return -1
        if (b.status === 'online' && a.status !== 'online') return 1
        return (a.hostname ?? '').localeCompare(b.hostname ?? '')
      })
    } catch (e) {
      console.error('[visibleMachines] crash:', e)
      return []
    }
  }, [machines])

  if (!isLoaded) return <div style={{ height: '100vh', background: 'var(--bg-primary)' }} />
  if (!isSignedIn) return <Navigate to="/sign-in" replace />

  const handleGenerateToken = async (): Promise<{ token: string; machine_id: string }> => {
    const authToken = await getToken()
    const res = await fetch(`${GATEWAY_HTTP}/api/tokens`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
      },
      body: JSON.stringify({}),
    })
    if (!res.ok) throw new Error('Failed to generate token')
    const data = await res.json() as { token: string; machine_id: string }
    return { token: data.token, machine_id: data.machine_id }
  }

  const pollMachineStatus = async (machineId: string): Promise<string | null> => {
    try {
      const authToken = await getToken()
      const res = await fetch(`${GATEWAY_HTTP}/api/machines/${machineId}`, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
      })
      if (!res.ok) return null
      const data = await res.json() as { status: string }
      return data.status ?? null
    } catch {
      return null
    }
  }

  const handleRemoveMachine = async (machineId: string) => {
    const token = await getToken()
    const res = await fetch(`${GATEWAY_HTTP}/api/machines/${machineId}`, {
      method: 'DELETE',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
    if (res.ok) await refetchMachines()
  }

  const handleConnect = (machineId: string) => {
    const machine = visibleMachines.find(m => m.id === machineId)
    const hostname = machine?.hostname ?? machineId
    if (!sessions.find(s => s.id === machineId)) {
      setSessions(prev => [...prev, { id: machineId, machineId, hostname }])
    }
    setActiveSessionId(machineId)
    setActiveView('terminal')
  }

  const handleCloseSession = (sessionId: string) => {
    const next = sessions.filter(s => s.id !== sessionId)
    setSessions(next)
    if (next.length === 0) {
      setActiveView('fleet')
      setActiveSessionId(null)
    } else if (activeSessionId === sessionId) {
      setActiveSessionId(next[next.length - 1].id)
    }
  }

  const handleAnalyze = async (output: string) => {
    try {
      const res = await fetch(`${GATEWAY_HTTP}/api/analyze`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ output, product_context: 'Stelline' }),
      })
      const data = await res.json()
      setAnalysis(data)
    } catch (err) {
      console.error('AI analysis failed:', err)
    }
  }

  const onlineMachines = visibleMachines.filter(m => m.status === 'online').length

  return (
    <div className="flex h-screen overflow-hidden" style={{ background: 'var(--bg-primary)' }}>

      {/* ── Sidebar ──────────────────────────────────────────────────────── */}
      <aside
        className="flex flex-col shrink-0"
        style={{ width: 224, background: 'var(--bg-sidebar)', borderRight: '1px solid var(--border)' }}
      >
        {/* Logo area */}
        <div
          className="shrink-0 flex items-center"
          style={{ height: 60, padding: '0 16px', borderBottom: '1px solid var(--border)' }}
        >
          <StellLogo iconSize={24} wordSize={13} showTagline={true} />
        </div>

        {/* Nav list */}
        <div className="flex-1 overflow-y-auto" style={{ padding: '8px 8px 0' }}>

          {/* ── FLEET section ─────────────────────────────────────────────── */}
          <SectionLabel>Fleet</SectionLabel>

          <button
            onClick={() => setActiveView('fleet')}
            style={{
              width: '100%', height: 36, display: 'flex', alignItems: 'center', gap: 8,
              padding: '0 10px', borderRadius: 6,
              background: activeView === 'fleet' ? 'var(--accent-surface)' : 'transparent',
              border: 'none',
              borderLeft: activeView === 'fleet' ? '2px solid var(--accent)' : '2px solid transparent',
              cursor: 'pointer', textAlign: 'left',
              transition: 'background 0.1s',
            }}
          >
            {/* Server icon */}
            <svg width={14} height={14} viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, opacity: 0.7 }}>
              <rect x="1" y="2" width="14" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
              <rect x="1" y="9" width="14" height="5" rx="1.5" stroke="currentColor" strokeWidth="1.2" />
              <circle cx="13" cy="4.5" r="1" fill={onlineMachines > 0 ? '#22c55e' : 'currentColor'} />
              <circle cx="13" cy="11.5" r="1" fill="currentColor" opacity="0.4" />
            </svg>
            <span className="truncate flex-1" style={{ fontSize: 13, color: 'var(--text-primary)', fontFamily: 'inherit' }}>
              All Machines
            </span>
            {/* Count badge */}
            {!machinesLoading && visibleMachines.length > 0 && (
              <span style={{
                fontSize: 10, fontWeight: 600, minWidth: 18, height: 16,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: 'rgba(255,255,255,0.08)',
                color: 'var(--text-secondary)',
                borderRadius: 99, padding: '0 4px', flexShrink: 0,
              }}>
                {visibleMachines.length}
              </span>
            )}
          </button>

          {/* ── SESSIONS section — only when sessions are open ─────────────── */}
          {sessions.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <SectionLabel>Sessions</SectionLabel>
              {sessions.map(session => (
                <div key={session.id} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                  <button
                    onClick={() => { setActiveSessionId(session.id); setActiveView('terminal') }}
                    style={{
                      flex: 1, height: 36, display: 'flex', alignItems: 'center', gap: 8,
                      padding: '0 28px 0 10px', borderRadius: 6,
                      background: activeView === 'terminal' && activeSessionId === session.id ? 'var(--accent-surface)' : 'transparent',
                      border: 'none',
                      borderLeft: activeView === 'terminal' && activeSessionId === session.id ? '2px solid var(--accent)' : '2px solid transparent',
                      cursor: 'pointer', textAlign: 'left',
                      transition: 'background 0.1s',
                    }}
                  >
                    <StatusDot status={activeSessionId === session.id ? termStatus : 'disconnected'} size={7} />
                    <span className="truncate" style={{ fontSize: 13, color: 'var(--text-primary)', fontFamily: 'inherit' }}>
                      {session.hostname}
                    </span>
                  </button>
                  <button
                    onClick={() => handleCloseSession(session.id)}
                    title="Close session"
                    style={{
                      position: 'absolute', right: 6, width: 18, height: 18,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: 'var(--text-dim)', fontSize: 16, borderRadius: 3,
                      lineHeight: 1, padding: 0,
                    }}
                  >×</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* User */}
        <SidebarUser />
      </aside>

      {/* ── Main area ────────────────────────────────────────────────────── */}
      <main className="flex-1 overflow-hidden" style={{ position: 'relative', background: 'var(--bg-primary)' }}>
        {activeView === 'fleet' ? (
          <ErrorBoundary>
            <FleetDashboard
              machines={visibleMachines}
              isLoading={machinesLoading}
              onConnect={handleConnect}
              onRemove={handleRemoveMachine}
              onGenerateToken={handleGenerateToken}
              pollMachineStatus={pollMachineStatus}
              onMachineConnected={() => { void refetchMachines() }}
              installBaseUrl={GATEWAY_HTTP}
            />
          </ErrorBoundary>
        ) : activeSessionId ? (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <TerminalPanel
              sessionId={activeSessionId}
              label={sessions.find(s => s.id === activeSessionId)?.hostname}
              onAnalyze={handleAnalyze}
              onStatusChange={setTermStatus}
            />

            {/* Analysis panel */}
            {analysis && (
              <div className="shrink-0 overflow-y-auto" style={{
                borderTop: '1px solid var(--border)',
                background: '#0d0f1a',
                padding: '14px 16px',
                maxHeight: 200,
              }}>
                <div className="flex items-center gap-2 mb-2.5">
                  <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.06em', color: 'var(--accent-muted)', textTransform: 'uppercase' }}>
                    AI Analysis
                  </span>
                  <span style={{
                    fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 99,
                    background:
                      analysis.severity === 'critical' ? 'rgba(239,68,68,0.15)'
                      : analysis.severity === 'warning' ? 'rgba(245,158,11,0.15)'
                      : 'rgba(34,197,94,0.15)',
                    color:
                      analysis.severity === 'critical' ? 'var(--danger)'
                      : analysis.severity === 'warning' ? '#f59e0b'
                      : 'var(--success)',
                  }}>
                    {analysis.severity}
                  </span>
                  <button
                    onClick={() => setAnalysis(null)}
                    style={{ marginLeft: 'auto', color: 'var(--text-dim)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 2 }}
                  >×</button>
                </div>

                {analysis.findings.length > 0 && (
                  <ul style={{ margin: 0, padding: 0, listStyle: 'none', marginBottom: 10 }}>
                    {analysis.findings.map((f, i) => (
                      <li key={i} style={{ display: 'flex', gap: 6, fontSize: 12, color: 'var(--text-secondary)', marginBottom: 3 }}>
                        <span style={{ color: '#f59e0b', flexShrink: 0 }}>›</span>{f}
                      </li>
                    ))}
                  </ul>
                )}

                {analysis.actions.length > 0 && (
                  <>
                    <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 4 }}>Suggested actions</div>
                    <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                      {analysis.actions.map((a, i) => (
                        <li key={i} style={{ display: 'flex', gap: 6, fontSize: 12, color: 'var(--accent-muted)', marginBottom: 3 }}>
                          <span style={{ flexShrink: 0 }}>→</span>{a}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            )}
          </div>
        ) : null}
      </main>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Auth pages
// ---------------------------------------------------------------------------
const clerkAppearance = {
  baseTheme: dark,
  variables: {
    colorBackground:      'transparent',
    colorInputBackground: 'rgba(255,255,255,0.04)',
    colorText:            '#ffffff',
    colorTextSecondary:   '#888888',
    colorPrimary:         '#7c6ff7',
    colorNeutral:         '#ffffff',
    borderRadius:         '8px',
    fontFamily:           'Inter, system-ui, sans-serif',
    fontSize:             '14px',
  },
  elements: {
    card:           { background: 'transparent', boxShadow: 'none', border: 'none', padding: 0 },
    header:         { display: 'none' },
    headerTitle:    { display: 'none' },
    headerSubtitle: { display: 'none' },
    logoBox:        { display: 'none' },
    footer:         { display: 'none' },
    footerAction:   { display: 'none' },
    footerPages:    { display: 'none' },
    formButtonPrimary:        { background: '#7c6ff7', borderRadius: '8px' },
    socialButtonsBlockButton: { background: 'rgba(255,255,255,0.04)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.08)' },
    formFieldInput:           { background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', color: '#ffffff' },
    dividerLine:              { background: 'rgba(255,255,255,0.06)' },
    dividerText:              { color: '#444444', fontSize: '12px' },
    formFieldLabel:           { color: '#888888', fontSize: '13px' },
    footerActionLink:         { color: '#a5b4fc' },
  },
} as const

interface AuthPageProps {
  children:        React.ReactNode
  title:           string
  subtitle:        string
  footerText:      string
  footerLinkLabel: string
  footerLinkHref:  string
}

function AuthPage({ children, title, subtitle, footerText, footerLinkLabel, footerLinkHref }: AuthPageProps) {
  return (
    <div className="auth-page">
      <div className="auth-orb" />
      <div className="auth-card">
        <div style={{ marginBottom: 24 }}>
          <StellLogo iconSize={26} wordSize={18} showTagline={true} />
        </div>
        <div className="auth-separator" />
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#ffffff', marginBottom: 8, letterSpacing: '-0.01em' }}>
            {title}
          </div>
          <div style={{ fontSize: 13, color: '#888888', lineHeight: 1.5 }}>
            {subtitle}
          </div>
        </div>
        {children}
        <div style={{ marginTop: 20, textAlign: 'center', fontSize: 13, color: '#555555' }}>
          {footerText}{' '}
          <a href={footerLinkHref} className="auth-footer-link">{footerLinkLabel}</a>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Root router
// ---------------------------------------------------------------------------
export default function App() {
  return (
    <Routes>
      <Route
        path="/sign-in/*"
        element={
          <AuthPage
            title="Welcome back"
            subtitle="Sign in to your account to continue"
            footerText="Don't have an account?"
            footerLinkLabel="Sign up"
            footerLinkHref="/sign-up"
          >
            <SignIn routing="path" path="/sign-in" fallbackRedirectUrl="/" appearance={clerkAppearance} />
          </AuthPage>
        }
      />
      <Route
        path="/sign-up/*"
        element={
          <AuthPage
            title="Create your account"
            subtitle="Start debugging smarter with AI-powered terminal analysis"
            footerText="Already have an account?"
            footerLinkLabel="Sign in"
            footerLinkHref="/sign-in"
          >
            <SignUp routing="path" path="/sign-up" fallbackRedirectUrl="/" appearance={clerkAppearance} />
          </AuthPage>
        }
      />
      <Route path="/*" element={<MainApp />} />
    </Routes>
  )
}
